import { useEffect, useState } from 'react';
import { Play, Square, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Field, Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/menu';
import { useActivities, useClients, useCreateActivity, useCreateClient } from '@/hooks/useReference';
import { useStartTimer, useStopTimer, useTimer } from '@/hooks/useEntries';
import { useBillingNouns } from '@/hooks/useSession';
import { elapsed } from '@/lib/format';
import { clientOptions } from '@/lib/client-labels';

/**
 * The visible clock ticks locally from `startedAt`; the query only polls
 * occasionally as a correctness backstop (the timer can also be stopped from
 * the iOS app or the CLI).
 */
function useTickingElapsed(startedAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return 0;
  return Math.max(0, (now - new Date(startedAt).getTime()) / 1000);
}

export function TimerWidget() {
  const { data: session, isPending } = useTimer();
  const seconds = useTickingElapsed(session?.startedAt);
  const stop = useStopTimer();

  if (isPending) {
    return <div className="skeleton h-9 w-40 rounded-xl" />;
  }

  if (!session) return <StartTimerButton />;

  return (
    <div className="flex items-center gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] py-1 pl-3 pr-1">
      <Timer className="size-4 text-[var(--accent)]" />
      <span className="tnum text-sm font-medium tabular-nums">{elapsed(seconds)}</span>
      <span className="max-w-40 truncate text-sm text-muted">
        {session.clientName ?? '—'} · {session.activityName ?? '—'}
      </span>
      <Button
        size="sm"
        variant="dangerGhost"
        onClick={() => stop.mutate()}
        loading={stop.isPending}
      >
        <Square />
        Stop
      </Button>
    </div>
  );
}

function StartTimerButton() {
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const nouns = useBillingNouns();

  // Options only load once the popover is opened.
  const clients = useClients();
  const activities = useActivities();
  const createClient = useCreateClient();
  const createActivity = useCreateActivity();
  const start = useStartTimer();

  const submit = () => {
    if (!clientId || !activityId) return;
    start.mutate({ clientId, activityId, note: note || undefined });
    setOpen(false);
    setNote('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary">
          <Play />
          Start timer
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <Field label={nouns.client}>
          <Combobox
            options={clientOptions(clients.data ?? [])}
            loading={clients.isPending}
            value={clientId}
            onChange={setClientId}
            onCreate={async (name) => {
              const created = await createClient.mutateAsync({ name });
              setClientId(created.id);
            }}
            createLabel={`Add ${nouns.clientLower}`}
            placeholder={`Select a ${nouns.clientLower}`}
          />
        </Field>
        <Field label="Activity">
          <Combobox
            options={(activities.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
            loading={activities.isPending}
            value={activityId}
            onChange={setActivityId}
            onCreate={async (name) => {
              const created = await createActivity.mutateAsync({ name });
              setActivityId(created.id);
            }}
            createLabel="Add activity"
            placeholder="Select an activity"
          />
        </Field>
        <Field label="Note" hint="Optional — becomes the entry's note when you stop.">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What are you working on?"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </Field>
        <Button
          variant="primary"
          className="w-full"
          disabled={!clientId || !activityId}
          onClick={submit}
        >
          <Play />
          Start
        </Button>
      </PopoverContent>
    </Popover>
  );
}
